import { format } from 'date-fns';

export const galleryImages = [
  { id: 1, name: 'gallery_img_1', label: 'Gallery Image 1', image: null, alt: '' },
  { id: 2, name: 'gallery_img_2', label: 'Gallery Image 2', image: null, alt: '' },
  { id: 3, name: 'gallery_img_3', label: 'Gallery Image 3', image: null, alt: '' },
];

export const initialFaqs = [
  { question: '', answer: '' },
  { question: '', answer: '' },
  { question: '', answer: '' },
];

export const initialFeaturedImages = [
  { id: 1, name: 'featured_img_1', label: 'Featured Image 1', file: null, alt: '' },
  { id: 2, name: 'featured_img_2', label: 'Featured Image 2', file: null, alt: '' },
  { id: 3, name: 'featured_img_3', label: 'Featured Image 3', file: null, alt: '' },
  { id: 4, name: 'featured_img_4', label: 'Featured Image 4', file: null, alt: '' },
];

export const initialMaterials = [
  {
    image: null,
    name: '',
    description: '',
    materialsSubheading: '',
    materialsSubheadingOptions: [{ value: '' }],
    characteristicHeading: '',
    characteristicOption: '',
  },
];

export const initialFinishes = [
  {
    image: null,
    name: '',
    description: '',
    characteristicHeading: '',
    characteristicOption: '',
  },
];

export const initialInserts = [
  {
    image: null,
    name: '',
    description: '',
    characteristicHeading: '',
    characteristicOption: '',
  },
];

/**
 * Maps a product from the API into the shape the form needs: the state each
 * section renders from, plus a flat `values` object for `form.setFieldsValue`.
 * Sections missing from the response are left out so the defaults survive.
 */
export const buildProductFormState = productData => {
  const values = {};
  const state = { values };

  // Gallery Images (Design Section)
  if (productData?.designSection?.files?.length) {
    state.gallery = productData.designSection.files.map((file, index) => ({
      id: index + 1,
      name: `gallery_img_${index + 1}`,
      label: `Gallery Image ${index + 1}`,
      image: file?.url || null,
      alt: file?.alt || '',
    }));

    state.gallery.forEach(field => {
      values[field.name] = field.image;
      values[`alt_${field.name}`] = field.alt;
    });
  }

  // Featured Images
  if (productData?.featureImages?.length) {
    state.featuredImages = productData.featureImages.map((item, index) => ({
      id: index + 1,
      name: `featured_img_${index + 1}`,
      label: `Featured Image ${index + 1}`,
      file: item?.image || null,
      alt: item?.alt || '',
    }));

    state.featuredImages.forEach(field => {
      values[field.name] = field.file;
      values[`alt_${field.name}`] = field.alt;
    });
  }

  // Materials
  if (productData?.materials?.length) {
    state.materials = productData.materials.map(mat => ({
      image: mat?.url || null,
      name: mat?.name || '',
      description: mat?.description || '',
      materialsSubheading: mat?.materialsSubheading || '',
      materialsSubheadingOptions:
        mat?.materialsSubheadingOptions?.length > 0 ? mat.materialsSubheadingOptions : [{ value: '' }],
      characteristicHeading: mat?.characteristicHeading || '',
      characteristicOption: mat?.characteristicOption || '',
    }));

    state.materials.forEach((mat, index) => {
      values[`material_image_${index + 1}`] = mat.image;
      values[`material_name_${index + 1}`] = mat.name;
      values[`material_description_${index + 1}`] = mat.description;
      values[`materialsSubheading_${index + 1}`] = mat.materialsSubheading;
      values[`material_characteristicHeading_${index + 1}`] = mat.characteristicHeading;
      values[`characteristicOption_${index + 1}`] = mat.characteristicOption;
      mat.materialsSubheadingOptions.forEach((thick, tIndex) => {
        values[`thickness_${index + 1}_${tIndex + 1}`] = thick.value;
      });
    });
  }

  // Finishes
  if (productData?.finishes?.length) {
    state.finishes = productData.finishes.map(finish => ({
      image: finish?.url || null,
      name: finish?.name || '',
      description: finish?.description || '',
      characteristicHeading: finish?.characteristicHeading || '',
      characteristicOption: finish?.characteristicOption || '',
    }));

    state.finishes.forEach((finish, index) => {
      values[`finish_image_${index + 1}`] = finish?.image;
      values[`finish_name_${index + 1}`] = finish?.name;
      values[`finish_description_${index + 1}`] = finish?.description;
      values[`finish_characteristicHeading_${index + 1}`] = finish?.characteristicHeading;
      values[`characteristicOption_${index + 1}`] = finish?.characteristicOption;
    });
  }

  // Inserts
  if (productData?.inserts?.length) {
    state.inserts = productData.inserts.map(insert => ({
      image: insert?.url || null,
      name: insert?.name || '',
      description: insert?.description || '',
      characteristicHeading: insert?.characteristicHeading || '',
      characteristicOption: insert?.characteristicOption || '',
    }));

    state.inserts.forEach((insert, index) => {
      values[`insert_image_${index + 1}`] = insert.image;
      values[`insert_name_${index + 1}`] = insert.name;
      values[`insert_description_${index + 1}`] = insert.description;
      values[`insert_characteristicHeading_${index + 1}`] = insert.characteristicHeading;
      values[`characteristicOption${index + 1}`] = insert.characteristicOption;
    });
  }

  // FAQs
  if (productData?.faqs?.length) {
    state.faqs = productData.faqs;

    state.faqs.forEach((faq, index) => {
      values[`faq_question_${index + 1}`] = faq.question;
      values[`faq_answer_${index + 1}`] = faq.answer;
    });
  }

  // Basic fields
  Object.assign(values, {
    title: productData?.title,
    sku: productData?.sku,
    category: productData?.categories?.map(i => ({ label: i?.title, value: i?._id })),
    industry: productData?.industries?.map(i => ({ label: i?.title, value: i?._id })),
    style: productData?.styles?.map(i => ({ label: i?.title, value: i?._id })),
    metaTitle: productData?.metaTitle,
    metaDescription: productData?.metaDescription,
    keywords: productData?.keywords,
    shortDescription: productData?.shortDescription,
    publishedDate: productData?.publishedDate ? format(new Date(productData.publishedDate), 'yyyy-MM-dd') : '',
    dieline_img: productData?.dieline?.image,
    dieline_image_alt: productData?.dieline?.alt,
    dieline_heading: productData?.dieline?.heading,
    dieline_description: productData?.dieline?.description,
    dieline_file: productData?.dieline?.zipFile,
    customization_heading: productData?.customization?.heading,
    customization_subHeading: productData?.customization?.subHeading,
    customization_description: productData?.customization?.description,
    design_section_heading: productData?.designSection?.heading,
    design_section_description: productData?.designSection?.description,
    personalization_sub_heading: productData?.personalization?.subHeading,
    personalization_heading: productData?.personalization?.heading,
    personalization_description: productData?.personalization?.description,
    personalization_image: productData?.personalization?.image,
    slug: productData?.slug,
  });

  return state;
};

const clean = obj => Object.fromEntries(Object.entries(obj).filter(([, v]) => v));

/** Builds the create/update request body from the form values and section state. */
export const buildProductPayload = ({
  data,
  featuredImageFields,
  galleryFields,
  materialCards,
  finishCards,
  insertCards,
  faqFields,
}) => {
  const namedMaterials = materialCards?.filter(m => m.name?.trim()) ?? [];
  const namedFinishes = finishCards?.filter(f => f.name?.trim()) ?? [];
  const namedInserts = insertCards?.filter(i => i.name?.trim()) ?? [];

  return clean({
    title: data?.title,
    sku: data?.sku,
    categories: data?.category?.map(i => i?.value) || null,
    industries: data?.industry?.map(i => i?.value) || null,
    styles: data?.style?.map(i => i?.value) || null,
    metaTitle: data?.metaTitle,
    metaDescription: data?.metaDescription,
    keywords: data?.keywords,
    shortDescription: data?.shortDescription,
    publishedDate: data?.publishedDate,
    featureImages: featuredImageFields?.map(i => i?.file) || null,
    featureImagesAlts: featuredImageFields?.map(i => i?.alt) || null,
    dielineImage: data?.dieline_img,
    dieline: {
      alt: data?.dieline_image_alt,
      heading: data?.dieline_heading,
      description: data?.dieline_description,
    },
    dielineZipFile: data?.dieline_file,
    customization: {
      heading: data?.customization_heading,
      subHeading: data?.customization_subHeading,
      description: data?.customization_description,
    },
    ...(namedMaterials.length > 0 && {
      materials: namedMaterials.map(({ image, ...rest }) => rest),
      materialsFiles: namedMaterials.map(item => item?.image),
    }),
    ...(namedFinishes.length > 0 && {
      finishes: namedFinishes.map(({ image, ...rest }) => rest),
      finishesFiles: namedFinishes.map(item => item?.image),
    }),
    ...(namedInserts.length > 0 && {
      inserts: namedInserts.map(({ image, ...rest }) => rest),
      insertsFiles: namedInserts.map(item => item?.image),
    }),
    designSection: {
      heading: data?.design_section_heading,
      description: data?.design_section_description,
    },
    designSectionFiles: galleryFields?.map(item => item?.image),
    designSectionFilesAlts: galleryFields?.map(item => item?.alt),
    personalization: {
      subHeading: data?.personalization_sub_heading,
      heading: data?.personalization_heading,
      description: data?.personalization_description,
    },
    personalizationImage: data?.personalization_image,
    faqs: faqFields,
  });
};
